import { FC, useCallback, useState } from 'react'
import clsx from 'clsx'
import { createIcon } from 'components/icons/IconFactory'

interface CardChoiceChoiceProps {
  iconKey?: string
  label: string
  onClick: (event?: React.MouseEvent) => void
  isSelected?: boolean
  className?: string
  disabled?: boolean
}

export const CardChoice: FC<CardChoiceChoiceProps> = ({
  iconKey,
  label,
  onClick,
  className,
  isSelected = false,
  disabled = false
}) => {
  const [isPressed, setIsPressed] = useState(false)

  const renderIcon = useCallback((key: string) => {
    const Icon = createIcon(key)
    return <Icon
      className={clsx(
        "w-full h-full grow transition-all duration-200 ease-out pointer-events-none",

        // Couleur de l'icône selon l'état
        isSelected
          ? "text-primary"
          : "text-primary/80",

        // Animation de l'icône au hover et clic
        "group-hover:scale-110",
        isSelected
          ? "group-hover:text-primary"
          : "group-hover:text-primary/90",

        isPressed && (isSelected ? "scale-95 text-primary/90" : "scale-95 text-primary/80"),

        // Effet de rebond pour les éléments sélectionnés
        isSelected && "drop-shadow-sm",

        // Styles pour l'état désactivé
        disabled && "opacity-50"
      )}
    />
  }, [isSelected, isPressed, disabled])

  const handleMouseDown = () => {
    if (!disabled) setIsPressed(true)
  }
  
  const handleMouseUp = () => setIsPressed(false)
  const handleMouseLeave = () => setIsPressed(false)

  const handleClick = (event: React.MouseEvent) => {
    // Empêcher la soumission du formulaire et la propagation
    event.preventDefault()
    event.stopPropagation()
    
    if (disabled) return
    
    onClick(event)
    // Petit délai pour voir l'effet de clic
    setTimeout(() => setIsPressed(false), 150)
  }

  return (
    <button
      type="button" // Explicitement définir le type pour éviter la soumission
      disabled={disabled}
      className={clsx(
        // Base styles
        className,
        "relative isolate flex flex-col items-center gap-2 justify-center p-4 rounded-xl",
        "border text-sm font-semibold transition-all duration-200 ease-out",
        "focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",

        // Styles conditionnels basés sur l'état sélectionné
        isSelected ? [
          // Styles pour l'état sélectionné
          "bg-primary/20",
          "shadow-md shadow-primary/20",
        ] : [
          // Styles pour l'état non sélectionné
          "border-transparent bg-primary/10",
        ],

        // Background layers pour les effets
        "before:absolute before:inset-0 before:-z-10 before:rounded-xl",
        "before:shadow-sm before:transition-all before:duration-200",
        isSelected
          ? "before:bg-primary/15"
          : "before:bg-primary/5",

        // Overlay pour hover et active states
        "after:absolute after:inset-0 after:-z-10 after:rounded-xl after:transition-all after:duration-200",

        // Hover effects - différents selon l'état sélectionné
        !isSelected && !disabled && [
          "hover:before:bg-primary/8 hover:after:bg-primary/5",
          "hover:border-primary/20 hover:shadow-md",
          "hover:-translate-y-0.5 hover:scale-[1.02]",
        ],

        isSelected && !disabled && [
          "hover:shadow-lg hover:shadow-primary/25",
          "hover:ring-primary/40",
          "hover:-translate-y-0.5 hover:scale-[1.02]",
        ],

        // Active/pressed effects
        isPressed && !disabled && [
          "translate-y-0 scale-[0.98]",
          isSelected
            ? "before:bg-primary/20 after:bg-primary/10 shadow-inner"
            : "before:bg-primary/12 after:bg-primary/8 shadow-inner border-primary/30"
        ],

        // Styles pour l'état désactivé
        disabled && [
          "opacity-50 cursor-not-allowed",
          "hover:transform-none hover:shadow-none"
        ],

        // Animation pour l'icône
        "group"
      )}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {
        iconKey &&
        <div className='w-16 h-16'>
          {renderIcon(iconKey)}
        </div>
      }
      <p className={clsx(
        "text-center text-sm transition-colors duration-200 grow",

        // Couleur du texte selon l'état
        isSelected
          ? "text-zinc-800"
          : "text-zinc-700",

        !disabled && "group-hover:text-zinc-800",
        isPressed && !disabled && "text-zinc-600",
        disabled && "opacity-50"
      )}>
        {label}
      </p>
    </button>
  )
}
